/**
 * @file LegalDocFields.tsx
 * @description Общие поля документа: название, адрес на сайте, краткое
 * описание (phase19.16, SB139, SB141; uiux-legal-docs §3–§4).
 *
 * Адрес задаётся при создании и потом не меняется (на него ведут меню,
 * подвал и письма; в PATCH адреса нет). У оферты и политики ПДн адрес на
 * сайте короткий (`/offer`, `/privacy`) — его показывает `safeLegalHref`.
 * Описание — у PDF и группы (у PDF обязательно для публикации, SB139);
 * у текста описания нет.
 *
 * Контракт `values / onChange` общий с остальными секциями карточки.
 */

import { LEGAL_DESCRIPTION_MAX_LENGTH, LEGAL_SLUG_MAX_LENGTH, LEGAL_SLUG_RULE_MESSAGE, LEGAL_TITLE_MAX_LENGTH, type LegalDocumentKind } from "@chaiberi/shared";
import { useId } from "react";

import { TextField } from "../site-view/TextField";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { safeLegalHref } from "../../lib/legal/safe-legal-href";
import type { LegalDraftErrors, LegalDraftValues } from "../../hooks/legal/useLegalDraftForm";

interface Props {
  kind: LegalDocumentKind;
  values: LegalDraftValues;
  onChange: (patch: Partial<LegalDraftValues>) => void;
  errors: LegalDraftErrors;
  /** Новый документ: адрес можно ввести. */
  isNew: boolean;
  disabled?: boolean;
}

/** Подсказка к описанию по виду. */
const DESCRIPTION_HINT: Readonly<Record<Exclude<LegalDocumentKind, "TEXT">, string>> = {
  PDF: "Обязательно для публикации: по нему документ находят поиском и читают программы для незрячих.",
  GROUP: "Вступление над списком документов на странице группы.",
};

export function LegalDocFields({ kind, values, onChange, errors, isNew, disabled }: Props) {
  const id = useId();
  const slug = values.slug.trim();
  // Во время ввода адрес бывает недописан («cert-») — предпросмотр только у валидного.
  const href = safeLegalHref(slug);
  return (
    <div className="flex flex-col gap-4">
      <TextField
        label={kind === "GROUP" ? "Название группы" : "Название"}
        value={values.title}
        onChange={(title) => onChange({ title })}
        max={LEGAL_TITLE_MAX_LENGTH}
        placeholder={kind === "GROUP" ? "Сертификаты" : "Сертификат соответствия ЕАЭС"}
        error={errors.title}
        disabled={disabled}
      />

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-slug`}>Адрес на сайте</Label>
        {isNew ? (
          <div className="flex items-center gap-1">
            <span className="type-body shrink-0 text-on-surface-variant">/legal/</span>
            <Input
              id={`${id}-slug`}
              value={values.slug}
              onChange={(e) => onChange({ slug: e.target.value.toLowerCase() })}
              placeholder="cert-2026"
              maxLength={LEGAL_SLUG_MAX_LENGTH}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={!!errors.slug}
              aria-describedby={`${id}-slug-hint`}
              disabled={disabled}
              className="min-w-0 flex-1"
            />
          </div>
        ) : (
          <p id={`${id}-slug`} className="type-body break-words text-on-surface">
            {href ?? `/legal/${slug}`}
          </p>
        )}
        <p id={`${id}-slug-hint`} className="type-caption text-on-surface-variant">
          {isNew
            ? `${href ? `На сайте: ${href}.` : LEGAL_SLUG_RULE_MESSAGE} После создания адрес не меняется — на него ведут ссылки.`
            : "Адрес не меняется — на него ведут меню, подвал и письма покупателям."}
        </p>
        {errors.slug ? (
          <p role="alert" className="type-caption text-destructive">
            {errors.slug}
          </p>
        ) : null}
      </div>

      {kind !== "TEXT" ? (
        <TextField
          label="Краткое описание"
          value={values.description}
          onChange={(description) => onChange({ description })}
          max={LEGAL_DESCRIPTION_MAX_LENGTH}
          placeholder={kind === "GROUP" ? "Сертификаты и декларации на нашу продукцию" : "Сертификат на травяные сборы, действует до 2027 года"}
          hint={DESCRIPTION_HINT[kind]}
          error={errors.description}
          multiline
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}
